import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PortableText } from '@portabletext/react';
import imageUrlBuilder from '@sanity/image-url';
import { DateTime } from 'luxon';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Slider from 'react-slick';
import { ArrowRight, ArrowRightShort } from '../../assets/icons';
import {
  blogPills,
  fbIcon,
  instaIcon,
  linkedinIcon,
  twitterIcon,
  youTubeIcon,
  LogoNew,
} from '../../assets/images';
import BottomImageTwo from '../../components/bottomImageTwo';
import Ribbon from '../../components/ribbon';
import { client } from '../../utils/GROQ';
import {
  blogPostingSchema,
  breadcrumbSchema,
  jsonLd,
  medicalWebPageSchema,
  SITE_URL,
} from '../../utils/schema';

const builder = imageUrlBuilder(client);
function urlFor(source) {
  return builder.image(source);
}
const myPortableTextComponents = {
  types: {
    image: ({ value }) => {
      try {
        const url = urlFor(value).width(820).auto('format')?.url();

        return (
          // null
          <div className=' position-relative mb-4' style={{ width: '100%' }}>
            <img
              // layout='fill'
              alt={value.alt || ''}
              // objectFit='contain'
              src={url}
              // quality={80}
              // width={820}
              // height={464}
            />
          </div>
        );
      } catch {
        return null;
      }
    },
    image_with_alt: ({ value }) => {
      try {
        const url = urlFor(value.image).width(820).auto('format')?.url();

        return (
          // null
          <div className=' position-relative mb-4' style={{ width: '100%' }}>
            <img
              // layout='fill'
              alt={value.alt}
              // objectFit='contain'
              src={url}
              // quality={80}
              // width={820}
              // height={464}
            />
          </div>
        );
      } catch {
        return null;
      }
    },
    // Sanity's table type: { rows: [{ _type: 'tableRow', cells: [string] }] }.
    // Without this the whole table is dropped silently, which is what was
    // happening on the posts that already use it.
    table: ({ value }) => {
      const rows = Array.isArray(value?.rows) ? value.rows : [];
      if (rows.length === 0) return null;

      // The first row is the header, which is how the editor enters them.
      const [head, ...body] = rows;
      const cellsOf = (row) => (Array.isArray(row?.cells) ? row.cells : []);
      // Short rows would otherwise collapse the table, so pad to the widest.
      const width = rows.reduce((max, r) => Math.max(max, cellsOf(r).length), 0);
      const pad = (row) => {
        const cells = cellsOf(row);
        return Array.from({ length: width }, (_, i) => cells[i] ?? '');
      };

      return (
        <div className='articleTableWrap'>
          <table className='articleTable'>
            <thead>
              <tr>
                {pad(head).map((cell, i) => (
                  <th key={i} scope='col'>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, r) => (
                <tr key={row?._key ?? r}>
                  {pad(row).map((cell, i) => (
                    <td key={i}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    youtube_video: ({ value }) => {
      const regex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const match = value.youtube_url.match(regex);

      if (match && match[1]) {
        const videoId = match[1];
        const embeded = `https://www.youtube.com/embed/${videoId}`;
        return (
          <iframe
            width='100%'
            style={{ aspectRatio: '16/9', marginBottom: '1rem' }}
            src={embeded}
            title='YouTube video player'
            frameborder='0'
            allow='clipboard-write; encrypted-media; gyroscope; web-share'
            referrerpolicy='strict-origin-when-cross-origin'
            allowfullscreen
          ></iframe>
        );
      }
      return null;
    },

    // callToAction: ({value, isInline}) =>
    //   isInline ? (
    //     <a href={value.url}>{value.text}</a>
    //   ) : (
    //     <div className="callToAction">{value.text}</div>
    //   ),
  },
};
const options = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};
var settings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        initialSlide: 2,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};
const Post = (props) => {
  let article = props.post;

  let articleDate =
    article.publishedAt == null
      ? new Date().setDate(new Date().getDate() - 7)
      : article.publishedAt;

  const postUrl = `${SITE_URL}/blog/${article?.slug?.current}`;
  // Mirrors the visible trail in the article header. Three levels is correct
  // here, unlike condition pages: /blog is a real page, whereas the condition
  // URLs' /[category] level 404s.
  const trail = [
    { name: 'Home', url: `${SITE_URL}/` },
    { name: 'Blog', url: `${SITE_URL}/blog` },
    { name: article?.title },
  ];

  const router = useRouter();
  const shareUrl = `https://www.24hrdoc.com/blog/${article?.slug?.current}`;
  const shareTitle = article?.title || '';
  useEffect(() => {
    client
      .patch(article._id) // Document ID to patch
      .inc({ views: 1 }) // Increment field by count
      .commit() // Perform the patch and return a promise
      .then((article) => {})
      .catch((err) => {
        console.error('Oh no, the update failed: ', err);
      });
  }, [article._id]);

  if (router.isFallback)
    <div>
      <h2>Loading...</h2>
    </div>;
  else
    return (
      <div>
        <Head>
          <title>{article.title} - 24hrdoc</title>
          <meta
            key={'description'}
            name='description'
            content={article.description}
          />
          <meta name='keywords' content={article.keywords} />
          <meta property='og:title' content={article?.title} />
          <meta property='og:type' content='article' />
          <meta
            property='og:url'
            content={`https://www.24hrdoc.com/blog/${article?.slug?.current}`}
          />
          <meta property='og:image' content={article.imageUrl} />
          <link rel='icon' href='/favicon.ico' />
          {/* The CMS schemajson field is deliberately no longer rendered:
              editors pasted blocks that duplicated the markup below. The
              sitewide Organization and WebSite entities live in _document.js
              and were previously repeated here, so every post carried them
              twice. */}
          <script
            type='application/ld+json'
            key='blogposting-jsonld'
            dangerouslySetInnerHTML={jsonLd(
              blogPostingSchema({
                url: postUrl,
                headline: article?.title,
                description: article?.description,
                image: article?.imageUrl,
                // Falls back to the organisation only when the CMS author
                // field is empty.
                authorName: article?.author,
                datePublished: DateTime.fromJSDate(
                  new Date(articleDate)
                ).toFormat('yyyy-MM-dd'),
                dateModified: article?._updatedAt
                  ? DateTime.fromJSDate(
                      new Date(article._updatedAt)
                    ).toFormat('yyyy-MM-dd')
                  : null,
                articleBody: article.schema?.articleBody || '',
              })
            )}
          />
          <script
            type='application/ld+json'
            key='webpage-jsonld'
            dangerouslySetInnerHTML={jsonLd(
              medicalWebPageSchema({
                name: article?.title,
                url: postUrl,
                description: article?.description,
                breadcrumbId: `${postUrl}#breadcrumb`,
              })
            )}
          />
          <script
            type='application/ld+json'
            key='breadcrumb-jsonld'
            dangerouslySetInnerHTML={jsonLd(breadcrumbSchema(trail, postUrl))}
          />

          <link
            rel='canonical'
            href={`https://www.24hrdoc.com/blog/${article?.slug?.current}`}
          />
        </Head>
        <div className='containerArticle'>
          <Container fluid className='px-0 blogHeader'>
            <Row className='px-0 blogHeaderRow'>
              <Col className='px-0' xs={12} lg={11} xl={9}>
                <div className='headerArticle'>
                  <div className='inner'>
                    <div className='navigation'>
                      <Link href='/'>
                        <span style={{ color: '#53575A99', cursor: 'pointer' }}>
                          Home
                        </span>
                      </Link>
                      <FontAwesomeIcon
                        icon={faAngleRight}
                        size='sm'
                        style={{ color: '#53575A99', strokeWidth: '1px' }}
                      />
                      <Link href='/blog'>
                        <span style={{ color: '#53575A99', cursor: 'pointer' }}>
                          Blog
                        </span>
                      </Link>
                      <FontAwesomeIcon
                        icon={faAngleRight}
                        size='sm'
                        style={{ color: '#53575A99', strokeWidth: '1px' }}
                      />
                      <span
                        className='textCut1'
                        style={{ color: '#53575A', cursor: 'pointer' }}
                      >
                        {article.title}
                      </span>
                    </div>
                    <h1 className=' mb-3'>{article.title}</h1>
                    {/* <p>Want to get treated for it?</p> */}
                    {/* <Link href={article.link !== null ? article.link : '/services'}>
                <button> Get Treated Now</button>
              </Link> */}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>

          <Container fluid className='midBody'>
            <Row className='px-0'>
              <Col md={8}>
                <div className='articleImage'>
                  <Image
                    src={article.imageUrl}
                    alt={article.title}
                    sizes=' (max-width:576px) 55vw,100vw'
                    layout='fill'
                    quality={80}
                  />
                </div>{' '}
                <div className='articleBody'>
                  <div style={{}} className='authorTagAndDate'>
                    <span style={{ marginBottom: '0' }}>
                      {article?.author ? article.author : '24hrdoc Team'}
                    </span>
                    <span
                      style={{
                        color: '#0000005C',
                        width: '0.5px',
                        fontWeight: '400',
                      }}
                    >
                      |
                    </span>
                    <span>Common Medical</span>
                    <span
                      style={{
                        color: '#0000005C',
                        width: '0.5px',
                        fontWeight: '400',
                      }}
                    >
                      |
                    </span>
                    <span className='publishedAt' style={{ marginBottom: '0' }}>
                      {new Date(articleDate).toLocaleString('en-US', options)}
                    </span>
                  </div>

                  <PortableText
                    value={article.body}
                    components={myPortableTextComponents}
                  />
                  <div className='btmSocialLinks'>
                    <p className='shareLabel'>Share this article</p>
                    <div className='shareRow'>
                      {socialLinks.map((item) => {
                        const isShare = !!item.shareType;
                        const href = getShareHref(item, shareUrl, shareTitle);
                        return (
                          <a
                            key={item.id}
                            href={href}
                            className='shareBtn'
                            aria-label={
                              isShare
                                ? `Share on ${item.platform}`
                                : `Follow us on ${item.platform}`
                            }
                            data-tooltip={
                              isShare
                                ? `Share on ${item.platform}`
                                : `Follow on ${item.platform}`
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={
                              isShare
                                ? (e) => {
                                    e.preventDefault();
                                    window.open(
                                      href,
                                      'share-popup',
                                      'width=600,height=520'
                                    );
                                  }
                                : undefined
                            }
                          >
                            <Image
                              src={item.src}
                              width={22}
                              height={22}
                              quality={80}
                              alt=''
                            />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <style jsx>{`
                    .btmSocialLinks {
                      display: flex;
                      flex-direction: column;
                      gap: 0.9rem;
                    }
                    .shareLabel {
                      margin: 0;
                      font-size: 0.95rem;
                      font-weight: 600;
                      letter-spacing: 0.02em;
                      color: #0f0f0f;
                    }
                    .shareRow {
                      display: flex;
                      flex-direction: row;
                      align-items: center;
                      column-gap: 0.75rem;
                    }
                    .shareBtn {
                      position: relative;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 44px;
                      height: 44px;
                      border-radius: 999px;
                      background: #ffffff;
                      border: 1.5px solid #e0e0e0;
                      transition: transform 0.2s ease, border-color 0.2s ease,
                        box-shadow 0.2s ease;
                    }
                    .shareBtn:hover,
                    .shareBtn:focus-visible {
                      transform: translateY(-3px);
                      border-color: transparent;
                      box-shadow: 0 0 0 2px #ffffff,
                        0 0 0 3.5px #1f6bb3,
                        0 8px 16px -6px rgba(31, 107, 179, 0.35);
                      outline: none;
                    }
                    .shareBtn:hover :global(img),
                    .shareBtn:focus-visible :global(img) {
                      transform: scale(1.08);
                    }
                    .shareBtn :global(img) {
                      transition: transform 0.2s ease;
                    }
                    .shareBtn::after {
                      content: attr(data-tooltip);
                      position: absolute;
                      bottom: calc(100% + 10px);
                      left: 50%;
                      transform: translateX(-50%) translateY(4px);
                      white-space: nowrap;
                      background: #0f0f0f;
                      color: #ffffff;
                      font-size: 0.75rem;
                      font-weight: 500;
                      padding: 5px 10px;
                      border-radius: 8px;
                      opacity: 0;
                      pointer-events: none;
                      transition: opacity 0.2s ease, transform 0.2s ease;
                    }
                    .shareBtn:hover::after,
                    .shareBtn:focus-visible::after {
                      opacity: 1;
                      transform: translateX(-50%) translateY(0);
                    }
                    @media (prefers-reduced-motion: reduce) {
                      .shareBtn,
                      .shareBtn :global(img),
                      .shareBtn::after {
                        transition: none;
                      }
                    }
                  `}</style>
                </div>
              </Col>
              <Col md={4}>
                {' '}
                <div
                  className='colMostViewed px-0'
                  style={{ marginTop: '5rem' }}
                >
                  <h2 className='heading'>Related Content</h2>

                  {props?.mostViewed.map((post) => (
                    <Link
                      href={{
                        pathname: `/blog/${post.slug.current}`,
                      }}
                      key={post.slug.current}
                    >
                      <div className='article' style={{ cursor: 'pointer' }}>
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          width={140}
                          height={120}
                          quality={80}
                        />

                        <div className='rightPart'>
                          <h2 className='textCut2'>{post.title}</h2>
                          <Link href='#'>
                            <span
                              style={{
                                color: '#195D9D',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                              }}
                            >
                              Read More
                              <span className='cta-arrows'>
                                <ArrowRight
                                  width={20}
                                  height={20}
                                  stroke='#195D9D'
                                />
                              </span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div
                  className='tw-flex tw-flex-col tw-items-center tw-mt-0 tw-mb-16 lg:tw-mt-[1.75rem] tw-rounded-xl tw-overflow-hidden tw-w-full tw-sticky tw-top-6'
                  style={{
                    boxShadow:
                      '0 8px 32px rgba(25, 93, 157, 0.18), 0 1.5px 6px rgba(0,0,0,0.08)',
                    border: '1.5px solid #e2eaf4',
                  }}
                >
                  {/* Logo strip */}
                  <div className='tw-bg-white tw-py-4 tw-px-6 tw-flex tw-justify-center tw-w-full'>
                    <Image
                      src={LogoNew}
                      width={400}
                      height={90}
                      objectFit='contain'
                      alt='24hrDoc logo'
                    />
                  </div>
                  {/* Gradient body */}
                  <div
                    className='tw-flex tw-flex-col tw-gap-y-5 tw-pt-4 tw-px-6 tw-pb-10 tw-w-full'
                    style={{
                      background:
                        'linear-gradient(135deg, #3FC1A2 0%, #195D9D 100%)',
                    }}
                  >
                    <h4 className='tw-text-[2.5rem] tw-font-medium tw-text-white tw-leading-tight tw-m-0 tw-text-center'>
                      Get the care
                      <br /> you need.
                    </h4>
                    <p className='tw-text-base tw-text-white tw-m-0 tw-leading-relaxed tw-text-center tw-px-4'>
                      Skip the waiting room and connect with a licensed provider
                      online from the comfort of home.
                    </p>
                  </div>
                  <Link href='/services#conditions'>
                    <button className='tw-text-[1.15rem] tw-font-semibold tw-text-white tw-bg-[#111827] tw-py-[0.65rem] tw-px-8 tw-rounded-full tw-cursor-pointer tw-border-none tw-flex tw-items-center tw-gap-x-2 tw-w-max tw-justify-center tw-self-center tw-my-6'>
                      Get Treatment <ArrowRightShort />
                    </button>
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
          {/* Laetst/You may also like */}
          <div className='latestArticlesContainer'>
            <div className='latestArticles'>
              <h2 style={{}} className='youMayAlsoLike'>
                You may also like
              </h2>
              <Slider {...settings}>
                {props?.latest?.map((post, i) => (
                  <div
                    key={i}
                    className=' px-2 py-2 rounded-2 d-flex flex-column justify-content-between'
                    style={{ height: '530px' }}
                  >
                    <Link
                      href={{
                        pathname: `/blog/${post.slug.current}`,
                      }}
                    >
                      <a>
                        <div
                          className='position-relative'
                          style={{
                            overflow: 'hidden',
                            cursor: 'pointer',
                          }}
                        >
                          <Image
                            src={post?.imageUrl}
                            alt={post?.title}
                            layout='responsive'
                            objectFit='cover'
                            className='imageZoom'
                            height={250}
                            width={425}
                          />
                        </div>
                      </a>
                    </Link>
                    <div className='tagAndDate'>
                      <span>Common Medical</span>
                      <span style={{ width: '0.5px', color: '#0000005C' }}>
                        |
                      </span>
                      <span>
                        {/* {post?.publishedAt
                          ? post.publishedAt
                          : new Date().toDateString()} */}
                        {post?.publishedAt
                          ? new Date(post.publishedAt).toLocaleDateString(
                              'en-US',
                              {
                                //weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              }
                            )
                          : new Date().toDateString()}{' '}
                      </span>
                    </div>

                    <Link
                      href={{
                        pathname: `/blog/${post.slug.current}`,
                      }}
                    >
                      <a>
                        <h2
                          className=' articleHeading textCut2'
                          style={{ marginTop: '1rem' }}
                        >
                          {post?.title}
                        </h2>
                      </a>
                    </Link>

                    <div
                      className='postBody'
                      //style={{ fontSize: '16px', fontFamily: 'inter' }}
                    >
                      <PortableText
                        value={
                          post?.body?.length > 1 ? post?.body?.[0] : post?.body
                        }
                      />
                    </div>
                    {/* <div className=' d-flex justify-content-between mt-2'>
                      {post.body ? (
                        <p
                          className=' text-capitalize'
                          style={{ color: '#195c9d', fontSize: '14px' }}
                        >
                          {Math.ceil(getReadingTime(toPlainText(post.body)))}{' '}
                          minute read
                        </p>
                      ) : null}
                    </div> */}
                    <div style={{ marginTop: '0.75rem' }}>
                      {' '}
                      <Link
                        href={{
                          pathname: `/blog/${post.slug.current}`,
                        }}
                      >
                        <a>
                          <p
                            style={{ width: '130px', whiteSpace: 'nowrap' }}
                            className='theReadMoreButton'
                          >
                            Read More
                          </p>
                        </a>
                      </Link>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </div>
        <Ribbon />
        <BottomImageTwo />
      </div>
    );
};

export default Post;
export const getStaticPaths = async () => {
  // defined(slug.current) matters: a half-started post in the CMS has no slug,
  // and post.slug.current below then throws during `next build`, failing the
  // whole deploy. One blank draft was enough to break it.
  const totalPosts = await client.fetch(
    '*[_type == "post" && defined(slug.current)]{slug}'
  );

  let paths = totalPosts.map((post) => {
    return {
      params: {
        id: post.slug.current,
      },
    };
  });

  return {
    paths: paths,
    fallback: 'blocking', // false or "blocking"
  };
};
export async function getStaticProps(context) {
  const post = await client.fetch(
    `*[_type == "post" && slug.current == '${context.params.id}']  { _id, "imageUrl": mainImage.asset->url,body,slug,title,keywords,publishedAt,views,description,link,schema_markup,heading,schema,schemajson,_updatedAt,"author":author->name}`
  );
  const latest = await client.fetch(
    `*[_type == "post"] | order(_createdAt) { _id, "imageUrl": mainImage.asset->url,title,slug,body,publishedAt}[0...4]`
  );
  const mostViewed = await client.fetch(
    '*[_type == "post" && !(_id in path("drafts.**"))] | order(views desc) {slug,title,"imageUrl": mainImage.asset->url,publishedAt}[0...5]'
  );

  if (post.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: { post: post[0], latest: latest, mostViewed: mostViewed },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 60, // In seconds
  };
}
// Facebook/Twitter/LinkedIn get a `shareType` so the component can build a
// real share-intent URL (share THIS article) instead of linking straight to
// our own profile page. Instagram/YouTube have no web share-intent for a
// URL, so those stay as follow links to our channels.
const socialLinks = [
  {
    id: 1,
    src: fbIcon,
    platform: 'Facebook',
    shareType: 'facebook',
    link: 'https://www.facebook.com/people/24-HR-DOC/100091804954061/?mibextid=ZbWKwL',
  },
  {
    id: 2,
    src: instaIcon,
    platform: 'Instagram',
    link: 'https://www.instagram.com/24hrdoc_/?igshid=YmMyMTA2M2Y%3D',
  },
  {
    id: 3,
    src: twitterIcon,
    platform: 'X',
    shareType: 'twitter',
    link: 'https://x.com/24HRDoc1',
  },
  {
    id: 4,
    src: linkedinIcon,
    platform: 'LinkedIn',
    shareType: 'linkedin',
    link: 'https://www.linkedin.com/feed/',
  },
  {
    id: 5,
    src: youTubeIcon,
    platform: 'YouTube',
    link: 'https://www.youtube.com/channel/UCYxLi6Vy_afwIda8HA_hV8Q',
  },
];

function getShareHref(item, shareUrl, shareTitle) {
  const encodedUrl = encodeURIComponent(shareUrl);
  switch (item.shareType) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(
        shareTitle
      )}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    default:
      return item.link;
  }
}

// "<script type=""application/ld+json"">
// {
//   ""@context"": ""https://schema.org"",
//   ""@type"": ""BlogPosting"",
//   ""headline"": ""A Simple Guide to Ear Infections and Online Treatments"",
//   ""description"": ""This guide explains ear infections, their symptoms, treatments, and prevention strategies, as well as how to access online treatments through 24hrdoc."",
//   ""author"": {
//     ""@type"": ""Organization"",
//     ""name"": ""24hrdoc""
//   },
//   ""publisher"": {
//     ""@type"": ""Organization"",
//     ""name"": ""24hrdoc"",
//     ""logo"": {
//       ""@type"": ""ImageObject"",
//       ""url"": ""https://www.24hrdoc.com/assets/icons/LogoTransparent.png""
//     }
//   },
//   ""datePublished"": ""2024-01-03"",
//   ""dateModified"": ""2024-01-03"",
//   ""mainEntityOfPage"": ""https://www.24hrdoc.com/blog/a-simple-guide-to-ear-infections-and-online-treatments"",

// }
// </script>
// "

// Bronchitis, a respiratory ailment characterized by inflammation of the bronchial tubes in the lungs, casts its shadow over 3.6% of males and 2.6% of females across 29 nations. The World Health Organization (WHO) sheds light on the global impact of this disease, revealing that approximately 2.9% of the world's population grapples with bronchitis. While bronchitis does not discriminate based on age, certain demographics face heightened susceptibility. Research from the National Center for Biotechnology (NCBI) suggests a higher susceptibility among males compared to females.\n\nAmidst the pervasive presence of bronchitis, the importance of accessible and effective treatment avenues escalates. Embracing the convenience and efficiency of online consultations at 24HrDoc for bronchitis emerges as a beacon of hope, offering swift access to professional medical advice and care from the confines of one's abode. This innovative approach ensures timely interventions and empowers individuals to address respiratory concerns promptly, fostering a journey towards enhanced well-being and respiratory health.\n\nBronchitis: Causes, Signs, Symptoms, and Precautionary Measures\nBronchitis is a respiratory illness that can be characterized by heavy congestion in the lungs, irritation in the throat, and coughing that brings up yellow phlegm and mucus. Patients usually experience inflammation in the bronchial tubes and the lungs. This could be due to regular exposure to polluted air, cigarette smoke, or minute dust particles. The constant irritation of bronchial tubes causes the person to experience physical or mental distress for long periods.\n\nAcute bronchitis is usually preventable and easily treatable. It is caused by exposure to contagious viruses and bacteria that live in contaminated air. It is also known as cold chest.\n\nChronic bronchitis occurs when exposure to stimulants such as air pollutants, harmful gases, and cigarette smoke increases significantly, and the symptoms persist for more than three months. After this period, it can build up to pneumonia.\n\nKey symptoms of bronchitis include:\n\nStuffy, runny nostrils\nGrass-level fever, fatigue, body ache\nThe congestion of the chest\nBreathing with wheezing or a whirring sound\nCough that has the potential to secrete yellow or green mucus.\nFeeling exhausted or run down.\nCan you lower the risk of getting affected by bronchitis? YES!\nAdhering to some of the following preventive habits can lower the risk of potential bronchitis illness.\n\nPracticing good hygiene\nAvoiding smoking tobacco products\nWearing a face mask\nWashing hands before having a meal\nGet a vaccine shot for pneumonia\nEating a healthy balanced diet\nStay hydrated\nRole of Online Doctors for Bronchitis Treatment\nOnline doctors can play a significant role in providing quick relief and effective bronchitis treatment. Through remote treatment, they can deliver prompt medical advice, prescribe essential medications, and offer the right assistance throughout treatment.\n\nBenefits associated with online bronchitis treatment.\n\nQuicker Resolution\nCost-effective\nEasily accessible\nSmooth follow-ups\nPersonalized treatment plan\nQuality treatment at low costs and more accessibility for people with mobility issues and geographical restrictions are just a few of the many advantages of online bronchitis treatment. Online consultations also make it easy to follow up, which is crucial for maintaining continuity of care and getting the most out of your treatments.\n\nHow to get Quick Online Bronchitis Treatment\n24HrDoc can step up to assist you with streamlined diagnosis and personalized treatment. To avail of quick treatment, follow the steps mentioned below:\n\nSubmit the online medical consultation form- Provide us with the list of symptoms, your medical history, and your current health condition.\nReceive Personalized 1-on-1 care- Our doctor will make sure to devise a treatment plan within hours.\nGet a prescription at your nearest pharmacy- We will offer a prescription you can pick up at your local pharmacy.\nUsually, acute bronchitis lasts only for a week, but the discomfort that it may cause can be treated with over-the-counter medications, such as:\n\nCough suppressants\nPain relievers\nAnti-inflammatory\nAnti-biotics\nTo treat chronic bronchitis, consult a medical practitioner who can develop a treatment plan that includes symptom relief and preventing complications.\n\nBronchitis Treatment Online with 24HrDOC\nAt 24HrDoc, our mission revolves around upholding top-tier healthcare standards while catering to a spectrum of medical needs. Beyond our proficiency in addressing conditions like bronchitis and various common health ailments, we are committed to delivering premium medical services and personalized treatment regimens tailored to each patient's unique needs.\n\nNavigating the realm of online healthcare, the 24HrDoc platform streamlines the patient’s experience, offering seamless connectivity to seasoned healthcare professionals. Here, individuals can effortlessly access expert medical advice, personalized treatment plans, and prescriptions – all within reach, anytime, anywhere. Witness the future of healthcare delivery unfold at your fingertips with 24HrDoc, where excellence meets convenience for your well-being.
